/**
 * BlueBean API の記録(速報／締め)を、日次【集計】と**同じ形の行**(BB ID × 架電会社)に直す(2026-09-21。D-120)。
 *
 * これで CSV を取り込む前でも、日次【集計】を **アポインター別のまま** 見られる。
 * 出せるのは **取得件数・時間帯別・通話回数** と、そこから出る 取得率・売上見込 まで。
 * 実稼働・受付・通話時間・後処理・報酬・原価率は **API に無い**ので 0 のまま入れ、画面側で「—」にする。
 * 確定値は これまでどおり CSV 取込(`daily_summary`)で、この行は置き換えられる。
 */
import { HOUR_BANDS, type Company, type DailySummary, type DateStr, type MonthTargets, type Operator, type StaffDaily } from '@core/lib/types';
import { resolveCompanyByCode } from '@core/import/activity';
import { staffMetrics, type StaffMetrics } from './calc';

/** API の1業務ぶん(bb_api_live / bb_api_daily で同じ形) */
export type LiveCodeStat = { calls?: number; success?: number; appointments?: number; by_hour?: number[] };
export type LiveApiDoc = {
  status?: string;
  window_to?: string;
  operators?: Record<string, { name?: string; by_code?: Record<string, LiveCodeStat> }>;
  /**
   * 通話明細(CDR)から出した **人ごと** の通話。キーは「氏名(BB ID)」。
   * 19時の締め(`bb_api_daily`)にだけ入る(日中の速報には通話明細が無い)
   */
  cdr_operators?: Record<string, { calls?: number; talked?: number; talk_sec?: number; first_at?: string; last_at?: string }>;
};

/** 「氏名(BB ID)」の形から BB ID を取り出す */
function bbIdOfCdrKey(key: string): string | null {
  const m = /\(([^)]+)\)\s*$/.exec(key.trim());
  return m ? (m[1] ?? '').trim().toUpperCase() : null;
}

/** 人ごとの通話時間(秒)。BB ID(大文字)で引く */
export function talkSecByBbId(doc: LiveApiDoc | null): Map<string, number> {
  const out = new Map<string, number>();
  for (const [key, v] of Object.entries(doc?.cdr_operators ?? {})) {
    const bb = bbIdOfCdrKey(key);
    if (!bb) continue;
    out.set(bb, (out.get(bb) ?? 0) + (v.talk_sec ?? 0));
  }
  return out;
}

/** API の内部ID → オペレーターと BB アカウント */
function accountByInternalId(operators: Operator[]): Map<string, { op: Operator; account: Operator['bb_accounts'][number] }> {
  const m = new Map<string, { op: Operator; account: Operator['bb_accounts'][number] }>();
  for (const op of operators) for (const a of op.bb_accounts ?? []) if (a.bb_internal_id) m.set(String(a.bb_internal_id), { op, account: a });
  return m;
}

export function buildLiveStaffRows(args: {
  doc: LiveApiDoc | null;
  date: DateStr;
  operators: Operator[];
  companies: Company[];
  companyMap: Map<string, Company>;
  targets: MonthTargets;
}): StaffMetrics[] {
  const { doc, date, operators, companies, companyMap, targets } = args;
  if (!doc) return [];
  const byId = accountByInternalId(operators);
  const talkByBb = talkSecByBbId(doc);
  /** 通話時間は人ごとにしか無いので、取得の多い業務の行に付ける(オペレーターレポートと同じ扱い) */
  const talkUsed = new Set<string>();
  // staffMetrics は summary の日付と単価表しか見ないので、その2つだけ持つ入れ物を渡す
  const summary = { id: date, date, staff: [], unit_prices: {}, note: null } as unknown as DailySummary;
  const rows: StaffMetrics[] = [];

  for (const [internalId, opDoc] of Object.entries(doc.operators ?? {})) {
    const hit = byId.get(internalId);
    if (!hit) continue; // 内部IDがマスタに無い人は出せない(オペレーターマスタで紐付けが要る)
    const { op, account } = hit;
    const codes = Object.entries(opDoc.by_code ?? {}).sort((a, b) => (b[1].appointments ?? 0) - (a[1].appointments ?? 0));
    for (const [code, stat] of codes) {
      const appointments = stat.appointments ?? 0;
      const calls = stat.success ?? stat.calls ?? 0;
      if (appointments === 0 && calls === 0) continue;
      const company = resolveCompanyByCode(code, companies);
      const base: StaffDaily = {
        op_id: op.id,
        op_code: op.op_code,
        name: op.name,
        bb_id: account.bb_id,
        work_style: account.work_style,
        pay_type: account.pay_type,
        business: account.business,
        company_id: company?.id ?? null,
        wage: account.current_wage ?? 0,
        appointments,
        by_hour: HOUR_BANDS.map((_, i) => stat.by_hour?.[i] ?? 0),
        calls,
        // 通話時間は締めの通話明細(CDR)にだけある。人ごとなので取得の多い業務の行に付ける
        reception_sec: 0,
        talk_sec: (() => {
          const bb = (account.bb_id ?? '').toUpperCase();
          if (talkUsed.has(bb)) return 0;
          const sec = talkByBb.get(bb) ?? 0;
          if (sec > 0) talkUsed.add(bb);
          return sec;
        })(),
        after_sec: 0,
        after_count: 0,
        login_sec: 0,
        break_sec: 0,
        away_sec: 0,
        shift_hours: null,
        break_hours: null,
        worked_hours: 0,
        hours_source: 'bb',
        commute: 0,
        commute_base: 0,
        pay: 0,
      } as StaffDaily;
      rows.push(staffMetrics(base, summary, targets, companyMap));
    }
  }
  return rows;
}

/** 見出しに出す「どこまでの数字か」。例: `速報 15:30時点` / `締めの結果(19時)` */
export function liveAsOf(doc: LiveApiDoc | null, kind: '速報' | '締め'): string {
  if (kind === '締め') return '締めの結果(19時)';
  const w = (doc?.window_to ?? '').slice(0, 5);
  return w ? `速報 ${w}時点` : '速報 取得中';
}
